import { useRef, type KeyboardEvent } from "react";
import classNames from "classnames";

import Icon from "@/components/Icon";
import type { MaterialNames } from "@/components/material_symbol_names";

export type SelectableNavListOption = {
  value: string;
  label: string;
  iconName?: MaterialNames;
};

export type SelectableNavListProps = {
  options: SelectableNavListOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
};

export default function SelectableNavList({
  options,
  value,
  onChange,
  label,
  disabled = false,
  className,
  "data-testid": testId = "selectable-nav-list"
}: SelectableNavListProps) {
  const labelId = label ? `${testId}-label` : undefined;
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const matchedIndex = options.findIndex((option) => option.value === value);
  const focusIndex = matchedIndex >= 0 ? matchedIndex : 0;

  const selectOption = (index: number) => {
    const option = options[index];
    if (!option || disabled) return;
    onChange(option.value);
    optionRefs.current[index]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (disabled || options.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        selectOption((index + 1) % options.length);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        selectOption((index - 1 + options.length) % options.length);
        break;
      case "Home":
        event.preventDefault();
        selectOption(0);
        break;
      case "End":
        event.preventDefault();
        selectOption(options.length - 1);
        break;
      case " ":
      case "Enter":
        event.preventDefault();
        selectOption(index);
        break;
      default:
        break;
    }
  };

  return (
    <div className={classNames("flex flex-col gap-2", className)} data-testid={testId}>
      {label ? (
        <p id={labelId} className="text-title-small-bold text-sys-brand-on-surface-variant">
          {label}
        </p>
      ) : null}
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        aria-disabled={disabled || undefined}
        className={classNames("flex flex-col gap-1", {
          "opacity-50 pointer-events-none": disabled
        })}
      >
        {options.map((option, index) => {
          const isSelected = option.value === value;

          return (
            <div
              key={option.value}
              ref={(node) => {
                optionRefs.current[index] = node;
              }}
              role="radio"
              aria-checked={isSelected}
              tabIndex={disabled ? -1 : index === focusIndex ? 0 : -1}
              data-testid={`${testId}-option-${option.value}`}
              onClick={() => selectOption(index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={classNames(
                "w-full rounded-lg border px-3 py-3 text-left transition-colors cursor-pointer",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-sys-brand-primary",
                {
                  "border-sys-brand-primary bg-sys-brand-surface": isSelected,
                  "border-transparent bg-transparent hover:bg-sys-brand-surface-container-low": !isSelected
                }
              )}
            >
              <span className="flex items-center gap-3">
                {option.iconName ? (
                  <Icon
                    name={option.iconName}
                    size={20}
                    className={isSelected ? "text-sys-brand-primary" : "text-sys-brand-on-surface-variant"}
                  />
                ) : null}
                <span
                  className={classNames("text-body-medium", {
                    "font-semibold text-sys-brand-primary": isSelected,
                    "text-sys-brand-on-surface": !isSelected
                  })}
                >
                  {option.label}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
